import { useMutation } from '@apollo/react-hooks';
import { REFERRAL_CAMPAIGN_CODE, UnexpectedError } from '@bowtie-web/common';
import {
  GET_OR_CREATE_REFERRAL_COUPON,
  ReferralCoupon,
} from '@bowtie-web/common/lib/graphql';
import { useEffect } from 'react';
import { useAsyncFn } from 'react-use';
import getLogger from '../logging';

const logger = getLogger('coupon');

/** Reads the signed-in customer's referral coupon code, creating one if none exists. */
const useReferralCouponCode = ({ skip }: { skip: boolean }) => {
  const [getOrCreate] = useMutation<{ createReferralCoupon: ReferralCoupon }>(
    GET_OR_CREATE_REFERRAL_COUPON,
  );

  const [state, run] = useAsyncFn(
    async (shouldSkip: boolean) => {
      if (shouldSkip) {
        return undefined;
      }

      try {
        const { data } = await getOrCreate({
          variables: { input: { campaign_code: REFERRAL_CAMPAIGN_CODE } },
        });

        return data?.createReferralCoupon?.coupon?.code || undefined;
      } catch (error) {
        const code = (
          error as { networkError?: { result?: { code?: string } } }
        )?.networkError?.result?.code;

        // A customer the campaign turns down has no code to apply.
        if (
          ![
            'invalid_referrer',
            'campaign_not_effective',
            'no_referral_coupon_created',
          ].includes(code ?? '')
        ) {
          logger.raise(
            new UnexpectedError(
              `Failed to get or create referral coupon: ${error}`,
            ),
          );
        }

        return undefined;
      }
    },
    [getOrCreate],
  );

  useEffect(() => {
    run(skip);
  }, [run, skip]);

  return [state.value, { loading: !skip && state.loading }] as const;
};

export default useReferralCouponCode;
